import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Sprout } from 'lucide-react'
import { convictionApi } from '../api/client'
import type { ConvictionRoster, RosterCandidate, RosterDossier } from '../types'
import { METRICS } from '../lib/metrics'
import { composeWhyLine, sentimentOf, SENTIMENT_LABEL } from '../lib/rosterWhy'
import { InfoTip } from './InfoTip'
import './IncubationRosterPanel.css'

interface IncubationRosterPanelProps {
  accountId: number
}

function fmtSignedPct(v: number | null | undefined): { text: string; cls: string } {
  if (v === null || v === undefined || !Number.isFinite(v)) {
    return { text: '—', cls: 'muted' }
  }
  const pct = v * 100
  const sign = pct >= 0 ? '+' : ''
  return { text: `${sign}${pct.toFixed(2)}%`, cls: pct >= 0 ? 'delta-up' : 'delta-down' }
}

/**
 * Percent-native variant of fmtSignedPct. Task 3's roster shadow fields
 * (`shadow_return_pct` / `shadow_excess_vs_spy_pct`) are served in PERCENT
 * units already (10.0 == +10%) — unlike the fraction-based fields the rest
 * of this panel formats, so this must NOT multiply by 100 again.
 */
function fmtSignedPctFromPct(v: number | null | undefined): { text: string; cls: string } {
  if (v === null || v === undefined || !Number.isFinite(v)) {
    return { text: '—', cls: 'muted' }
  }
  const sign = v >= 0 ? '+' : ''
  return { text: `${sign}${v.toFixed(2)}%`, cls: v >= 0 ? 'delta-up' : 'delta-down' }
}

/** Whole-percent rounding for the ranking-scale trend-excess magnitude. */
function fmtTrendPct(v: number | null | undefined): { text: string; cls: string } {
  if (v === null || v === undefined || !Number.isFinite(v)) {
    return { text: '—', cls: 'muted' }
  }
  const pct = Math.round(v * 100)
  const sign = pct >= 0 ? '+' : ''
  return { text: `${sign}${pct}%`, cls: v >= 0 ? 'delta-up' : 'delta-down' }
}

const YOY_FLAG_META: Record<string, { label: string; cls: string }> = {
  loss_to_profit: { label: 'loss → profit', cls: 'roster-yoy-pill--gain' },
  profit_to_loss: { label: 'profit → loss', cls: 'roster-yoy-pill--loss' },
  loss_to_loss: { label: 'loss → loss', cls: 'roster-yoy-pill--warn' },
}

/**
 * Renders a YoY figure. When the raw ratio was a misleading sign-crossing
 * value the backend nulls the number and sets a flag instead; we surface that
 * as a labeled pill. Used by both the table columns and the drill-down block.
 */
function YoYValue({
  value,
  flag,
}: {
  value: number | null | undefined
  flag: string | null | undefined
}) {
  if (value !== null && value !== undefined && Number.isFinite(value)) {
    const f = fmtSignedPct(value)
    return <span className={`num ${f.cls}`}>{f.text}</span>
  }
  if (flag) {
    const meta = YOY_FLAG_META[flag]
    if (meta) {
      return <span className={`roster-yoy-pill ${meta.cls}`}>{meta.label}</span>
    }
  }
  return <span className="muted">—</span>
}

/** Trend-excess magnitude: rounded signed % over a sign-colored strength bar. */
function TrendExcessCell({ value, maxAbs }: { value: number; maxAbs: number }) {
  const t = fmtTrendPct(value)
  const width = Math.min(100, (Math.abs(value) / maxAbs) * 100)
  const sign = value >= 0 ? 'pos' : 'neg'
  return (
    <div className="roster-trend">
      <span className={`num ${t.cls}`}>{t.text}</span>
      <div className="roster-trend-track" aria-hidden="true">
        <div
          className={`roster-trend-fill roster-trend-fill--${sign}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  )
}

/**
 * Shadow-performance-since-flag stack: excess vs SPY (primary, sign-colored)
 * over the raw shadow return (secondary, de-emphasized). Both fields are
 * percent-native (see fmtSignedPctFromPct) and both em-dash together when
 * pricing was unavailable for this candidate.
 */
function ShadowCell({ cand }: { cand: RosterCandidate }) {
  const excess = fmtSignedPctFromPct(cand.shadow_excess_vs_spy_pct)
  const ret = fmtSignedPctFromPct(cand.shadow_return_pct)
  return (
    <div className="roster-shadow">
      <span className={`num ${excess.cls}`}>{excess.text}</span>
      <span className="caption roster-shadow-return num">{ret.text}</span>
    </div>
  )
}

function fmtBig(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  const fmt = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
  return `${sign}$${fmt.format(abs)}`
}

/** A −1…+1 sentiment dot with an accessible title. */
function SentimentDot({ meanKw }: { meanKw: number }) {
  const s = sentimentOf(meanKw)
  return (
    <span
      className={`roster-sentiment-dot roster-sentiment-dot--${s}`}
      title={`${SENTIMENT_LABEL[s]} (mean_kw ${meanKw.toFixed(2)})`}
      aria-hidden="true"
    />
  )
}

function IncubationCell({
  cand,
  confirmN,
  timeoutDays,
}: {
  cand: RosterCandidate
  confirmN: number
  timeoutDays: number
}) {
  if (cand.state !== 'incubating' || cand.days_incubating === null) {
    // Non-incubating rows (benched/entered/etc.) still carry a blocking_gate
    // — "awaiting bench re-score (Mon)" for benched rows, and "entries
    // disabled" for ANY row once the kill switch is on — but the early
    // return above used to drop it on the floor entirely (IMPORTANT 2).
    // Render it next to the plain state text with the same styling the
    // incubating branch below uses.
    return (
      <span className="roster-state-plain">
        {cand.state}
        {cand.blocking_gate ? (
          <span
            className={`roster-gate-label${
              cand.blocking_gate === 'entries disabled' ? ' roster-gate-label--warn' : ''
            }`}
          >
            {' '}
            · {cand.blocking_gate}
          </span>
        ) : null}
      </span>
    )
  }
  const progress = Math.max(0, Math.min(1, cand.incubation_progress ?? 0))
  const timeout = timeoutDays || 1
  const tick = Math.max(0, Math.min(1, Math.min(confirmN, timeout) / timeout))
  return (
    <div className="roster-incubation">
      <div className="roster-incubation-track">
        <div className="roster-incubation-fill" style={{ width: `${progress * 100}%` }} />
        <span
          className="roster-incubation-tick"
          style={{ left: `${tick * 100}%` }}
          title="eligible"
          aria-label="eligible"
        />
      </div>
      <div className="roster-incubation-text caption">
        Day <span className="num">{cand.days_incubating}</span> · elig ~
        <span className="num">{confirmN}</span>d · stale {cand.stale_out_date ?? '—'}
        {cand.blocking_gate ? (
          <span
            className={`roster-gate-label${
              cand.blocking_gate === 'entries disabled' ? ' roster-gate-label--warn' : ''
            }`}
          >
            {' '}
            · {cand.blocking_gate}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Renders the dossier that is already carried on the roster row (no second
 * fetch): point-in-time fundamentals at flag, news sentiment, and the
 * keyword-vs-LLM A/B snapshot.
 */
function DossierDrilldown({
  dossier,
  sector,
}: {
  dossier: RosterDossier | null
  sector: string | null
}) {
  if (dossier === null) {
    return <div className="roster-drilldown-empty caption">no decision snapshot</div>
  }
  const sectorText = sector?.trim()
  const f = dossier.financials
  const meanKw = dossier.news.mean_kw
  const nTitles = dossier.news.n_titles
  const llmEntries = dossier.llm ? Object.entries(dossier.llm) : null

  return (
    <div className="roster-drilldown">
      <div className="roster-drilldown-block">
        <div className="roster-drilldown-title">Fundamentals at flag (PIT)</div>
        <dl className="roster-kv">
          <dt>Sector</dt>
          <dd>{sectorText ? sectorText : <span className="muted">—</span>}</dd>
          <dt>Fiscal period end</dt>
          <dd>{f.fiscal_period_end ?? <span className="muted">—</span>}</dd>
          <dt>Revenue</dt>
          <dd className="num">{fmtBig(f.revenue)}</dd>
          <dt>Net income</dt>
          <dd className="num">{fmtBig(f.net_income)}</dd>
          <dt>Revenue YoY</dt>
          <dd className="num">
            <YoYValue value={f.revenue_yoy} flag={f.revenue_yoy_flag} />
          </dd>
          <dt>Net income YoY</dt>
          <dd className="num">
            <YoYValue value={f.net_income_yoy} flag={f.net_income_yoy_flag} />
          </dd>
        </dl>
      </div>

      <div className="roster-drilldown-block">
        <div className="roster-drilldown-title">News sentiment</div>
        {nTitles === 0 ? (
          <div className="caption">no news at flag</div>
        ) : (
          <div className="roster-news-gauge">
            <div className="roster-gauge-track">
              <span className="roster-gauge-zero" aria-hidden="true" />
              <span
                className={`roster-gauge-marker roster-gauge-marker--${sentimentOf(meanKw)}`}
                style={{ left: `${((Math.max(-1, Math.min(1, meanKw)) + 1) / 2) * 100}%` }}
              />
            </div>
            <div className="roster-news-legend caption">
              <SentimentDot meanKw={meanKw} />
              <span>{SENTIMENT_LABEL[sentimentOf(meanKw)]}</span>
              <span className="muted">·</span>
              <span className="num">{nTitles}</span> headlines
            </div>
          </div>
        )}
      </div>

      <div className="roster-drilldown-block">
        <div className="roster-drilldown-title">Keyword vs LLM (A/B)</div>
        {llmEntries === null ? (
          <div className="caption">keyword-only (LLM did not score)</div>
        ) : (
          <dl className="roster-kv">
            {llmEntries.map(([k, v]) => (
              <div className="roster-kv-line" key={k}>
                <dt>{k}</dt>
                <dd>{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  )
}

function RosterRow({
  cand,
  confirmN,
  timeoutDays,
  maxAbs,
  expanded,
  onToggle,
}: {
  cand: RosterCandidate
  confirmN: number
  timeoutDays: number
  maxAbs: number
  expanded: boolean
  onToggle: () => void
}) {
  const fin = cand.dossier?.financials
  const news = cand.dossier?.news
  const hasNews = news !== undefined && news.n_titles > 0
  const sector = cand.sector?.trim()

  return (
    <>
      <tr className="roster-row" onClick={onToggle}>
        <td className="roster-cell-symbol">
          <div className="roster-symbol-stack">
            <span className="mono roster-symbol-ticker">{cand.symbol}</span>
            {sector ? <span className="roster-symbol-sector">{sector}</span> : null}
            <span className="roster-why caption">{composeWhyLine(cand)}</span>
          </div>
        </td>
        <td className="roster-cell-state">
          <span className={`roster-state-pill roster-state-pill--${cand.state}`}>{cand.state}</span>
        </td>
        <td className="roster-cell-trend" data-label="Trend excess">
          <TrendExcessCell value={cand.trend_excess} maxAbs={maxAbs} />
        </td>
        <td className="roster-cell-shadow" data-label="Shadow vs SPY">
          <ShadowCell cand={cand} />
        </td>
        <td className="roster-incubation-cell roster-cell-incub" data-label="Incubation">
          <IncubationCell cand={cand} confirmN={confirmN} timeoutDays={timeoutDays} />
        </td>
        <td className="roster-cell-yoy roster-cell-rev" data-label="Rev YoY">
          <YoYValue value={fin?.revenue_yoy} flag={fin?.revenue_yoy_flag} />
        </td>
        <td className="roster-cell-yoy roster-cell-ni" data-label="NI YoY">
          <YoYValue value={fin?.net_income_yoy} flag={fin?.net_income_yoy_flag} />
        </td>
        <td className="roster-cell-news" data-label="News">
          {hasNews && news ? (
            <span className="roster-news-cell">
              <SentimentDot meanKw={news.mean_kw} />
              <span className="num">{news.n_titles}</span>
            </span>
          ) : cand.dossier === null || !news ? (
            <span className="muted">—</span>
          ) : (
            <span className="muted roster-no-news">no news</span>
          )}
        </td>
        <td className="roster-expander-cell roster-cell-exp">
          <button
            type="button"
            className="roster-expander"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse dossier' : 'Expand dossier'}
            onClick={(e) => {
              e.stopPropagation()
              onToggle()
            }}
          >
            {expanded ? (
              <ChevronDown size={16} aria-hidden="true" />
            ) : (
              <ChevronRight size={16} aria-hidden="true" />
            )}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="roster-drilldown-row">
          <td colSpan={9}>
            <DossierDrilldown dossier={cand.dossier} sector={cand.sector} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Research-book pre-entry pipeline: incubating / benched / entered candidates
 * with their point-in-time dossier. The roster endpoint returns rows lexically
 * by state, so this table imposes no discovered→incubating→benched ordering.
 */
export function IncubationRosterPanel({ accountId }: IncubationRosterPanelProps) {
  const { data, isLoading, isError } = useQuery<ConvictionRoster>({
    queryKey: ['conviction-roster', accountId],
    queryFn: () => convictionApi.roster(accountId),
    retry: 1,
    refetchInterval: 30000,
    // IMPORTANT 5(c): the roster backs a per-request batch price fetch
    // (fan-out over every candidate + SPY), so duplicate fetch pressure from
    // this component remounting (e.g. tab/route churn) matters. staleTime
    // just under the 30s poll interval means a remount reuses the cached
    // data instead of firing an immediate duplicate request, while the
    // scheduled 30s poll still always refetches once the entry goes stale.
    staleTime: 25000,
  })

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (symbol: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(symbol)) next.delete(symbol)
      else next.add(symbol)
      return next
    })
  }

  const candidates = data?.candidates ?? []
  // confirm_n / incubation_timeout_days are roster-level, threaded down as
  // props so IncubationCell stays a pure function of its candidate.
  const confirmN = data?.confirm_n ?? 5
  const timeoutDays = data?.incubation_timeout_days ?? 21
  // Shared denominator for the relative-strength bars, computed once so each
  // cell's width is a fraction of the strongest pick's magnitude.
  const maxAbs = Math.max(1e-9, ...candidates.map((c) => Math.abs(c.trend_excess)))

  // Aggregate header: count of incubating candidates + their average shadow
  // excess vs SPY (percent-native — see fmtSignedPctFromPct). Deliberately
  // omits a promotion countdown: ResearchCadenceStrip already owns that.
  const incubatingCandidates = candidates.filter((c) => c.state === 'incubating')
  const shadowExcessValues = incubatingCandidates
    .map((c) => c.shadow_excess_vs_spy_pct)
    .filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v))
  const avgShadowExcess =
    shadowExcessValues.length > 0
      ? shadowExcessValues.reduce((sum, v) => sum + v, 0) / shadowExcessValues.length
      : null
  const aggregateText = data
    ? data.shadow_available === false
      ? `${incubatingCandidates.length} incubating · prices unavailable`
      : avgShadowExcess !== null
        ? `${incubatingCandidates.length} incubating · avg shadow excess ${fmtSignedPctFromPct(avgShadowExcess).text}`
        : `${incubatingCandidates.length} incubating · shadow pending prices`
    : null

  return (
    <div className="card incubation-roster-panel">
      <div className="card-header">
        <div className="incubation-roster-heading">
          <h3 className="card-title">
            <Sprout size={18} className="icon" aria-hidden="true" />
            Research Pipeline
          </h3>
          <span className="caption">Pre-entry candidates · incubating / benched / entered</span>
          {aggregateText ? <span className="caption roster-aggregate">{aggregateText}</span> : null}
        </div>
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Sprout size={28} aria-hidden="true" />
          </div>
          <div className="empty-text">roster unavailable</div>
        </div>
      ) : candidates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Sprout size={28} aria-hidden="true" />
          </div>
          <div className="empty-text">no candidates in the pipeline</div>
        </div>
      ) : (
        <div className="incubation-roster-scroll">
          <table className="incubation-roster-table">
            <thead>
              <tr>
                <th scope="col">Symbol</th>
                <th scope="col">State</th>
                <th scope="col">
                  {METRICS.trend_excess_126d.short}
                  <InfoTip metric="trend_excess_126d" />
                </th>
                <th scope="col">
                  {METRICS.shadow_excess_since_flag.short}
                  <InfoTip metric="shadow_excess_since_flag" />
                </th>
                <th scope="col">Incubation</th>
                <th scope="col">Rev YoY</th>
                <th scope="col">NI YoY</th>
                <th scope="col">News</th>
                <th scope="col" aria-label="Expand" />
              </tr>
            </thead>
            <tbody>
              {candidates.map((cand) => (
                <RosterRow
                  key={cand.symbol}
                  cand={cand}
                  confirmN={confirmN}
                  timeoutDays={timeoutDays}
                  maxAbs={maxAbs}
                  expanded={expanded.has(cand.symbol)}
                  onToggle={() => toggle(cand.symbol)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
